import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faClockRotateLeft,
  faCloudArrowUp,
  faFileLines,
  faFolderOpen,
  faMagnifyingGlass,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { getAuth } from 'firebase/auth';
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import swal from 'sweetalert';
import LeftBar from '../Components/LeftBar';
import Dashnav from '../Components/Dashnav';
import { db, storage } from '../firebase';
import './dash.css';
import './legalDocs.css';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const EMPTY_FORM = {
  documentName: '',
  category: '',
  description: '',
  referenceNumber: '',
  issuer: '',
  issueDate: '',
  expiryDate: '',
  reminderDays: '30',
  file: null
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const folderLabel = (value) => {
  const label = String(value || '').trim();
  return label || 'Unsorted';
};

const safeFileName = (value) => String(value || 'document').replace(/[^a-zA-Z0-9._-]/g, '_');

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      return value.toDate();
    }

    if (typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
  }

  const nextDate = new Date(value);
  return Number.isNaN(nextDate.getTime()) ? null : nextDate;
};

const formatDate = (value) => {
  const date = parseDateValue(value);
  if (!date) {
    return 'Not set';
  }

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatDateTime = (value) => {
  const date = parseDateValue(value);
  if (!date) {
    return 'Not set';
  }

  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatDateInputValue = (value) => {
  const date = parseDateValue(value);
  if (!date) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) {
    return 'Unknown size';
  }

  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getExpiryMeta = (docItem) => {
  const expiryDate = parseDateValue(docItem.expiryDate);

  if (!expiryDate) {
    return {
      status: 'no-expiry',
      tone: 'secondary',
      label: 'No expiry',
      detail: 'No expiry date set',
      daysLeft: null
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDay = new Date(expiryDate);
  expiryDay.setHours(0, 0, 0, 0);

  const daysLeft = Math.ceil((expiryDay.getTime() - today.getTime()) / MS_PER_DAY);

  if (daysLeft < 0) {
    const overdueDays = Math.abs(daysLeft);
    return {
      status: 'expired',
      tone: 'danger',
      label: 'Expired',
      detail: `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`,
      daysLeft
    };
  }

  if (daysLeft === 0) {
    return {
      status: 'expiring-soon',
      tone: 'warning',
      label: 'Expires today',
      detail: 'Expires today',
      daysLeft
    };
  }

  if (daysLeft <= 30) {
    return {
      status: 'expiring-soon',
      tone: 'warning',
      label: 'Expiring soon',
      detail: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
      daysLeft
    };
  }

  return {
    status: 'active',
    tone: 'success',
    label: 'Active',
    detail: `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
    daysLeft
  };
};

const statusBadgeVariant = (status) => {
  if (status === 'expired') return 'danger';
  if (status === 'expiring-soon') return 'warning';
  if (status === 'active') return 'success';
  return 'secondary';
};

const sortByExpiryUrgency = (left, right) => {
  const rank = {
    expired: 0,
    'expiring-soon': 1,
    active: 2,
    'no-expiry': 3
  };

  const rankDiff = rank[left.expiryMeta.status] - rank[right.expiryMeta.status];
  if (rankDiff !== 0) {
    return rankDiff;
  }

  const leftExpiry = parseDateValue(left.expiryDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const rightExpiry = parseDateValue(right.expiryDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  if (leftExpiry !== rightExpiry) {
    return leftExpiry - rightExpiry;
  }

  const leftCreated = parseDateValue(left.createdAt)?.getTime() ?? 0;
  const rightCreated = parseDateValue(right.createdAt)?.getTime() ?? 0;
  return rightCreated - leftCreated;
};

function LegalDocsComp() {
  const auth = getAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    const docsQuery = query(collection(db, 'legalDocs'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(docsQuery, (snapshot) => {
      const nextDocuments = [];

      snapshot.forEach((docItem) => {
        nextDocuments.push({ id: docItem.id, ...docItem.data() });
      });

      setDocuments(nextDocuments);
      setLoading(false);
    }, (error) => {
      console.error('Failed to load legal documents:', error);
      setDocuments([]);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const enrichedDocuments = useMemo(() => (
    documents.map((docItem) => ({
      ...docItem,
      folderName: folderLabel(docItem.category),
      expiryMeta: getExpiryMeta(docItem)
    }))
  ), [documents]);

  const folderOptions = useMemo(() => {
    const folderMap = new Map();

    enrichedDocuments.forEach((docItem) => {
      const key = normalizeText(docItem.folderName);
      const existing = folderMap.get(key) || {
        key,
        label: docItem.folderName,
        count: 0
      };

      existing.count += 1;
      folderMap.set(key, existing);
    });

    return [
      { key: 'all', label: 'All folders', count: enrichedDocuments.length },
      ...Array.from(folderMap.values()).sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
    ];
  }, [enrichedDocuments]);

  const filteredDocuments = useMemo(() => {
    const queryText = normalizeText(searchTerm);

    return enrichedDocuments
      .filter((docItem) => {
        if (selectedFolder !== 'all' && normalizeText(docItem.folderName) !== selectedFolder) {
          return false;
        }

        if (statusFilter !== 'all' && docItem.expiryMeta.status !== statusFilter) {
          return false;
        }

        if (!queryText) {
          return true;
        }

        const haystack = [
          docItem.documentName,
          docItem.description,
          docItem.folderName,
          docItem.referenceNumber,
          docItem.issuer,
          docItem.fileName
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(queryText);
      })
      .sort(sortByExpiryUrgency);
  }, [enrichedDocuments, searchTerm, selectedFolder, statusFilter]);

  const expiryWatchlist = useMemo(() => (
    enrichedDocuments
      .filter((docItem) => docItem.expiryMeta.status === 'expired' || docItem.expiryMeta.status === 'expiring-soon')
      .sort(sortByExpiryUrgency)
      .slice(0, 5)
  ), [enrichedDocuments]);

  const stats = useMemo(() => {
    const expired = enrichedDocuments.filter((docItem) => docItem.expiryMeta.status === 'expired').length;
    const expiringSoon = enrichedDocuments.filter((docItem) => docItem.expiryMeta.status === 'expiring-soon').length;
    const active = enrichedDocuments.filter((docItem) => docItem.expiryMeta.status === 'active').length;
    const noExpiry = enrichedDocuments.filter((docItem) => docItem.expiryMeta.status === 'no-expiry').length;

    return {
      total: enrichedDocuments.length,
      expired,
      expiringSoon,
      active,
      noExpiry
    };
  }, [enrichedDocuments]);

  const handleFieldChange = (field) => (event) => {
    const value = field === 'file' ? event.target.files?.[0] || null : event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
  };

  const openCreateModal = () => {
    if (uploading) {
      return;
    }

    setSelectedDocument(null);
    setEditingDocument(null);
    resetForm();
    setShowUploadModal(true);
  };

  const openEditModal = (docItem) => {
    if (uploading || !docItem) {
      return;
    }

    setSelectedDocument(null);
    setEditingDocument(docItem);
    setFormData({
      documentName: docItem.documentName || '',
      category: docItem.category || '',
      description: docItem.description || '',
      referenceNumber: docItem.referenceNumber || '',
      issuer: docItem.issuer || '',
      issueDate: formatDateInputValue(docItem.issueDate),
      expiryDate: formatDateInputValue(docItem.expiryDate),
      reminderDays: String(docItem.reminderDays || 30),
      file: null
    });
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    if (uploading) {
      return;
    }

    setEditingDocument(null);
    resetForm();
    setShowUploadModal(false);
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!formData.documentName.trim()) {
      swal('Missing document name', 'Please add a document name before uploading.', 'error');
      return;
    }

    if (!editingDocument && !formData.file) {
      swal('Missing file', 'Please choose a document file to upload.', 'error');
      return;
    }

    if (formData.file && Number(formData.file.size) > 20 * 1024 * 1024) {
      swal('File too large', 'Please upload a file smaller than 20MB.', 'warning');
      return;
    }

    if (formData.issueDate && formData.expiryDate) {
      const issueDate = parseDateValue(formData.issueDate);
      const expiryDate = parseDateValue(formData.expiryDate);

      if (issueDate && expiryDate && expiryDate < issueDate) {
        swal('Invalid dates', 'The expiry date cannot be earlier than the issue date.', 'error');
        return;
      }
    }

    const legalDocRef = editingDocument ? doc(db, 'legalDocs', editingDocument.id) : doc(collection(db, 'legalDocs'));
    const categoryName = folderLabel(formData.category);
    const reminderDays = Number(formData.reminderDays);
    const hadNewFile = Boolean(formData.file);
    let filePayload = {};

    setUploading(true);

    try {
      if (hadNewFile) {
        const storagePath = `legal-docs/${normalizeText(categoryName) || 'unsorted'}/${legalDocRef.id}/${Date.now()}_${safeFileName(formData.file.name)}`;
        const fileRef = storageRef(storage, storagePath);

        await uploadBytes(fileRef, formData.file, {
          contentType: formData.file.type || 'application/octet-stream',
          customMetadata: {
            documentName: formData.documentName.trim(),
            category: categoryName,
            issuer: formData.issuer.trim()
          }
        });

        const fileUrl = await getDownloadURL(fileRef);
        filePayload = {
          fileUrl,
          filePath: storagePath,
          fileName: formData.file.name,
          fileSize: formData.file.size,
          fileType: formData.file.type || ''
        };
      } else if (editingDocument) {
        filePayload = {
          fileUrl: editingDocument.fileUrl || '',
          filePath: editingDocument.filePath || '',
          fileName: editingDocument.fileName || '',
          fileSize: editingDocument.fileSize || 0,
          fileType: editingDocument.fileType || ''
        }
      }

      const basePayload = {
        documentName: formData.documentName.trim(),
        category: categoryName,
        description: formData.description.trim(),
        referenceNumber: formData.referenceNumber.trim(),
        issuer: formData.issuer.trim(),
        issueDate: formData.issueDate || null,
        expiryDate: formData.expiryDate || null,
        reminderDays: Number.isFinite(reminderDays) && reminderDays > 0 ? reminderDays : 30,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.uid || '',
        updatedByEmail: auth.currentUser?.email || '',
        ...filePayload
      };

      if (editingDocument) {
        await updateDoc(legalDocRef, basePayload);
      } else {
        await setDoc(legalDocRef, {
          ...basePayload,
          createdAt: serverTimestamp(),
          uploadedBy: auth.currentUser?.uid || '',
          uploadedByEmail: auth.currentUser?.email || ''
        });
      }

      setShowUploadModal(false);
      setEditingDocument(null);
      resetForm();
      swal(
        'Document saved',
        editingDocument ? 'The legal document has been updated.' : 'The legal document has been added to the vault.',
        'success'
      );
    } catch (error) {
      console.error('Failed to upload legal document:', error);
      swal('Upload failed', error.message || 'The document could not be saved right now.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const openDocument = (docItem) => {
    setSelectedDocument(docItem);
  };

  return (
    <>
      <div className="mainContainer">
        <LeftBar />
        <div className="theMainContainer">
          <Dashnav />
          <div className="legalDocsPage legalDocsMinimal">
            <header className="legalDocsHeader">
              <div>
                <span className="legalDocsEyebrow">Administration / Records</span>
                <h1>Legal documents</h1>
                <p>Store permits, contracts, policies, and compliance records with expiry tracking.</p>
              </div>
              <Button variant="dark" className="legalDocsUploadBtn" onClick={openCreateModal}>
                <FontAwesomeIcon icon={faCloudArrowUp} />
                <span>Upload document</span>
              </Button>
            </header>

            <section className="legalDocsOverview" aria-label="Document summary">
              <div className="legalDocsMetric">
                <span>Total documents</span>
                <strong>{loading ? <Spinner animation="border" role="status" /> : stats.total}</strong>
              </div>
              <div className="legalDocsMetric">
                <span>Active</span>
                <strong>{loading ? <Spinner animation="border" role="status" /> : stats.active}</strong>
              </div>
              <div className="legalDocsMetric">
                <span>Expiring soon</span>
                <strong>{loading ? <Spinner animation="border" role="status" /> : stats.expiringSoon}</strong>
              </div>
              <div className="legalDocsMetric">
                <span>Expired</span>
                <strong>{loading ? <Spinner animation="border" role="status" /> : stats.expired}</strong>
              </div>
            </section>

            <section className="legalDocsRegister">
              <div className="legalDocsRegisterHeader">
                <div>
                  <h2>Document register</h2>
                  <p>Search the vault or filter by folder and expiry status.</p>
                </div>
                <span className="legalDocsResultCount">{loading ? 'Loading…' : `${filteredDocuments.length} shown`}</span>
              </div>
              <div className="legalDocsContentLayout">
                <aside className="legalDocsFoldersPanel" aria-label="Document folders">
                  <div className="legalDocsSectionLabel">
                    <FontAwesomeIcon icon={faFolderOpen} /> Folders
                  </div>
                  <div className="legalDocsFolderList">
                    {folderOptions.map((folder) => (
                      <button
                        key={folder.key}
                        type="button"
                        className={`legalDocsFolderItem ${selectedFolder === folder.key ? 'legalDocsFolderItem--active' : ''}`}
                        onClick={() => setSelectedFolder(folder.key)}
                      >
                        <span>{folder.label}</span>
                        <strong>{folder.count}</strong>
                      </button>
                    ))}
                  </div>
                  <div className="legalDocsWatchlist">
                    <div className="legalDocsSectionLabel">
                      <FontAwesomeIcon icon={faTriangleExclamation} /> Expiry watchlist
                      <span>{expiryWatchlist.length} flagged</span>
                    </div>
                    {expiryWatchlist.length === 0 ? (
                      <div className="legalDocsWatchEmpty">
                        <FontAwesomeIcon icon={faClockRotateLeft} /> No documents are expired or expiring soon.
                      </div>
                    ) : (
                      <div className="legalDocsWatchItems">
                        {expiryWatchlist.map((docItem) => (
                          <button
                            key={docItem.id}
                            type="button"
                            className="legalDocsWatchItem"
                            onClick={() => openDocument(docItem)}
                          >
                            <span>{docItem.documentName || 'Untitled document'}</span>
                            <Badge bg={statusBadgeVariant(docItem.expiryMeta.status)}>{docItem.expiryMeta.label}</Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </aside>

                <div className="legalDocsMainPanel">
                  <div className="legalDocsToolbar">
                    <InputGroup className="legalDocsSearch">
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Search by name, folder, reference, issuer, or file"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        aria-label="Search legal documents"
                      />
                    </InputGroup>

                    <Form.Select
                      className="filterSelect legalDocsStatusSelect"
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      aria-label="Filter by expiry status"
                    >
                      <option value="all">All statuses</option>
                      <option value="active">Active</option>
                      <option value="expiring-soon">Expiring soon</option>
                      <option value="expired">Expired</option>
                      <option value="no-expiry">No expiry</option>
                    </Form.Select>
                  </div>

                  <div className="legalDocsTableWrap">
                  {loading ? (
                    <div className="legalDocsState"><Spinner animation="border" role="status" /><span>Loading documents...</span></div>
                  ) : filteredDocuments.length === 0 ? (
                    <div className="legalDocsEmpty">
                      <h3>{documents.length === 0 ? 'No documents uploaded' : 'No matching documents'}</h3>
                      <p>{documents.length === 0 ? 'Upload a document to begin organizing the vault.' : 'Try a different search, folder, or status.'}</p>
                    </div>
                  ) : (
                    <Table hover className="legalDocsTable">
                      <thead>
                        <tr>
                          <th>Document</th>
                          <th>Folder</th>
                          <th>Status</th>
                          <th>Issued</th>
                          <th>Expiry</th>
                          <th aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocuments.map((docItem) => (
                          <tr key={docItem.id} className="legalDocsRow" onClick={() => openDocument(docItem)}>
                            <td>
                              <div className="legalDocsDocTitle">
                                <strong>{docItem.documentName || 'Untitled document'}</strong>
                                <span>{docItem.description || 'No description added.'}</span>
                              </div>
                            </td>
                            <td>
                              <div className="legalDocsMetaStack">
                                <strong>{docItem.folderName}</strong>
                                <small>{docItem.referenceNumber ? `Ref: ${docItem.referenceNumber}` : 'No reference'}</small>
                              </div>
                            </td>
                            <td>
                              <Badge bg={statusBadgeVariant(docItem.expiryMeta.status)}>
                                {docItem.expiryMeta.label}
                              </Badge>
                            </td>
                            <td>{formatDate(docItem.issueDate)}</td>
                            <td>{docItem.expiryMeta.detail}</td>
                            <td className="legalDocsRowAction">
                              <div className="legalDocsRowActions">
                              <button
                                type="button"
                                className="appsViewBtn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openDocument(docItem);
                                }}
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                className="appsViewBtn legalDocsEditBtn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openEditModal(docItem);
                                }}
                              >
                                Edit
                              </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Modal show={showUploadModal} onHide={closeUploadModal} centered size="lg" contentClassName="legalDocsModal">
        <Modal.Header closeButton={!uploading}>
          <Modal.Title>{editingDocument ? 'Edit legal document' : 'Upload legal document'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpload}>
          <Modal.Body>
            <div className="legalDocsUploadHint">
              <FontAwesomeIcon icon={faFileLines} />
              <p>
                Add the key details once and the vault can help you search, renew, and audit documents later.
              </p>
            </div>

            <div className="legalDocsUploadGrid">
              <Form.Group>
                <Form.Label>Document name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. County permit, lease agreement"
                  value={formData.documentName}
                  onChange={handleFieldChange('documentName')}
                  required
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Folder / category</Form.Label>
                <Form.Control
                  type="text"
                  list="legal-docs-folder-options"
                  placeholder="Permits, contracts, policies, compliance"
                  value={formData.category}
                  onChange={handleFieldChange('category')}
                  autoComplete="off"
                />
                <Form.Text className="text-muted">
                  Start typing to pick an existing folder, or enter a new one.
                </Form.Text>
              </Form.Group>

              <Form.Group className="legalDocsWideField">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Add a short note about what this document covers and where it is used."
                  value={formData.description}
                  onChange={handleFieldChange('description')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Reference / document no.</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Optional reference number"
                  value={formData.referenceNumber}
                  onChange={handleFieldChange('referenceNumber')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Issued by</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="County office, regulator, landlord..."
                  value={formData.issuer}
                  onChange={handleFieldChange('issuer')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Issue date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.issueDate}
                  onChange={handleFieldChange('issueDate')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Expiry date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.expiryDate}
                  onChange={handleFieldChange('expiryDate')}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Reminder days before expiry</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="365"
                  value={formData.reminderDays}
                  onChange={handleFieldChange('reminderDays')}
                />
              </Form.Group>

              <Form.Group className="legalDocsWideField">
                <Form.Label>Document file</Form.Label>
                <Form.Control
                  key={`${showUploadModal ? 'legal-docs-upload-open' : 'legal-docs-upload-closed'}-${editingDocument?.id || 'new'}`}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                  onChange={handleFieldChange('file')}
                  required={!editingDocument}
                />
                <Form.Text className="text-muted">
                  {editingDocument
                    ? 'Leave this blank to keep the current file, or upload a replacement.'
                    : 'PDFs work best, but you can also upload common office and image formats.'}
                </Form.Text>
              </Form.Group>
            </div>
            <datalist id="legal-docs-folder-options">
              {folderOptions
                .filter((folder) => folder.key !== 'all')
                .map((folder) => (
                  <option key={folder.key} value={folder.label} />
                ))}
            </datalist>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeUploadModal} disabled={uploading}>
              Cancel
            </Button>
            <Button variant="dark" type="submit" disabled={uploading}>
              {uploading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faCloudArrowUp} />}
              <span>{uploading ? 'Saving...' : editingDocument ? 'Update document' : 'Save document'}</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(selectedDocument)} onHide={() => setSelectedDocument(null)} centered size="lg" contentClassName="legalDocsModal">
        {selectedDocument && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedDocument.documentName || 'Document details'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="legalDocsDetailGrid">
                <div className="legalDocsDetailPane">
                  <span>Folder</span>
                  <strong>{selectedDocument.folderName}</strong>
                  <small>{selectedDocument.referenceNumber ? `Ref: ${selectedDocument.referenceNumber}` : 'No reference number'}</small>
                </div>
                <div className="legalDocsDetailPane">
                  <span>Status</span>
                  <strong className={`legalDocsStatusText legalDocsStatusText--${selectedDocument.expiryMeta.tone}`}>
                    {selectedDocument.expiryMeta.label}
                  </strong>
                  <small>{selectedDocument.expiryMeta.detail}</small>
                </div>
                <div className="legalDocsDetailPane">
                  <span>Issued by</span>
                  <strong>{selectedDocument.issuer || 'Not set'}</strong>
                  <small>{selectedDocument.issueDate ? `Issued on ${formatDate(selectedDocument.issueDate)}` : 'Issue date not provided'}</small>
                </div>
                <div className="legalDocsDetailPane">
                  <span>Reminder window</span>
                  <strong>{Number(selectedDocument.reminderDays) || 30} days</strong>
                  <small>Before expiry</small>
                </div>
              </div>

              <div className="legalDocsDetailDescription">
                <span>Description</span>
                <p>{selectedDocument.description || 'No description added.'}</p>
              </div>

              <div className="legalDocsDetailMeta">
                <div>
                  <span>File</span>
                  <strong>{selectedDocument.fileName || 'Unknown file'}</strong>
                  <small>{formatFileSize(selectedDocument.fileSize)}</small>
                </div>
                <div>
                  <span>Uploaded</span>
                  <strong>{formatDateTime(selectedDocument.createdAt)}</strong>
                  <small>{selectedDocument.uploadedByEmail || 'No uploader recorded'}</small>
                </div>
                <div>
                  <span>Expiry</span>
                  <strong>{selectedDocument.expiryDate ? formatDate(selectedDocument.expiryDate) : 'No expiry'}</strong>
                  <small>{selectedDocument.expiryMeta.detail}</small>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              {selectedDocument.fileUrl && (
                <Button as="a" href={selectedDocument.fileUrl} target="_blank" rel="noreferrer" variant="outline-dark">
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  <span>Open file</span>
                </Button>
              )}
              <Button
                variant="outline-dark"
                onClick={() => openEditModal(selectedDocument)}
              >
                Edit
              </Button>
              <Button variant="dark" onClick={() => setSelectedDocument(null)}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
}

export default LegalDocsComp;
